import { useEffect, useMemo, useRef } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"
import moment from "moment/moment"
import Delete from "pages/discount/delete"
import Filter from "pages/discount/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { usePage } from "contexts/page-context"
import useDiscountRepositories from "repositories/discount"
import Table from "components/table"
import useBranchRepositories from "repositories/branch"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

export default function List() {
    // Logged in user ability
    const { can, isSuper, currentBranch } = useAuth()
    // Configuration
    const { configuration } = usePage()

    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router navigate hook
    const navigate = useNavigate()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const [searchTemp, setSearchTemp] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
    const name = searchParams.get('name')
    const from = searchParams.get('from')
    const to = searchParams.get('to')
    const branch = currentBranch ? currentBranch.id : searchParams.get('branch')

    const orderBy = searchParams.get('order_by')
    const direction = searchParams.get('direction')

    const { data: discountData, isLoading: isLoadingDiscountData, mutate: mutateDiscountData } = useDiscountRepositories({
        page,
        search,
        name,
        from,
        to,
        outlet_id:branch,
        order_by: orderBy,
        direction
    })

    const { data: branchData, isLoading: isLoadingBranchSelection } = useBranchRepositories({
        paginate: false
    })

    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    // Watch changes on petty cash data and loading state
    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoadingDiscountData) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [discountData, isLoadingDiscountData])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // Data sort handlers
    const toggleSort = (value) => {
        if ((direction === null || direction === 'desc') || orderBy !== value) {
            setSearchParams({
                order_by: value,
                direction: 'asc'
            })
        } else {
            setSearchParams({
                order_by: value,
                direction: 'desc'
            })
        }
    }
    
    const branchNameById = useMemo(() => {
        const list = branchData?.data ?? branchData ?? []; // jaga-jaga kalau shape-nya beda
        const map = new Map();
        for (const b of list) {
            map.set(String(b.id), b.name);
        }
        return map;
    }, [branchData]);

    const getOutletName = (outletId) => {
        if (outletId == null) return "-";
        return branchNameById.get(String(outletId)) ?? "-";
    };

    // Data list props
    const dataListHeaders = [
        {
            isAction: false,
            content: "Nama",
            sortKey: "name"
        },
        {
            isAction: false,
            content: "Outlet",
            sortKey: "outlet_id"
        },
        {
            isAction: false,
            content: "Sales Item Count",
            sortKey: "sales_item_count"
        },
        {
            isAction: false,
            content: "Starting Item Number",
            sortKey: "starting_item_number"
        },
        {
            isAction: false,
            content: "Percentage",
            sortKey: "percentage"
        },
        {
            isAction: false,
            content: "Amount",
            sortKey: "amount"
        },
        {
            isAction: false,
            content: "Multiply",
            sortKey: "multiply"
        },
        {
            isAction: false,
            content: "Dibuat Pada",
            sortKey: "created_at"
        },
        {
            isAction: false,
            content: "Diubah Pada",
            sortKey: "updated_at"
        },
        {
            isAction: true,
            content: null
        }
    ]

    const dataListColumns = [
        {
            type: "default",
            bold: true,
            content: (value) => value.name
        },
        {
            type: "default",
            bold: false,
            content: (value) => getOutletName(value.outlet_id)
        },
        {
            type: "default",
            bold: false,
            content: (value) => value.sales_item_count
        },
        {
            type: "default",
            bold: false,
            content: (value) => value.starting_item_number
        },
        {
            type: "default",
            bold: false,
            content: (value) => value.percentage ? `${value.percentage}%` : '-'
        },
        {
            type: "default",
            bold: false,
            content: (value) => value.amount ? Intl.NumberFormat('id-Id', { style: 'currency', currency: 'idr' }).format(value.amount) : '-'
        },
        {
            type: "default",
            bold: false,
            content: (value) => value.multiply ? 'Yes' : 'No'
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.created_at).format('MMMM D, YYYY')
        },
        {
            type: "default",
            bold: false,
            content: (value) => moment(value.updated_at).format('MMMM D, YYYY')
        },
        {
            type: "action",
            content: (value) => (
                <>
                    {can('discount.update') && (
                        <Link to={`${value.id}/edit`} state={{ back: title, from: location?.pathname, transition: 'slide' }} className="inline-flex items-center p-1 text-white transition rounded-full bg-neutral-800 active:hover:scale-90">
                            <Pencil className="w-6 h-6" />
                        </Link>
                    )}

                    {can('discount.delete') && (
                        <Delete data={value} onSuccess={() => mutateDiscountData()} />
                    )}
                </>
            )
        }
    ]

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchTemp(e.target.value)} value={searchTemp} type="text" placeholder={`${lang.search} Discount`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ name, from, to,branch }).filter(([_, v]) => v != null))} />
                </div>
                <div className="flex items-center space-x-2">
                    {can('discount.create') && (
                        <div>
                            <Link to="create" onClick={() => { setTransition({ type: 'slide', reverse: true }) }} state={{ back: title, from: location?.pathname, transition: { type: 'slide' } }} className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-neutral-800 rounded-xl active:hover:scale-90">
                                <Plus className="w-4 h-4" stroke={1.5} />
                                <span>{lang.create}</span>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            <Table isLoading={isLoadingDiscountData} data={discountData?.data ?? []} headers={dataListHeaders} columns={dataListColumns} key={(value) => value.id} isEmpty={discountData?.data.length === 0 && !search && !isLoadingDiscountData} isEmptyOnSearching={discountData?.data.length === 0 && search && !isLoadingDiscountData} toggleSort={toggleSort} orderBy={orderBy} direction={direction} />
            <Pagination links={discountData?.links} from={discountData?.from} to={discountData?.to} total={discountData?.total} />
        </div>
    )
}
